import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import { APP_VERSION } from '../../lib/version';

interface SidebarProps {
  activePage: string;
  onNavigate: (page: string) => void;
  userName: string;
  onLogout: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  permission?: { resource: string; action: string };
  roles?: string[];
  divider?: boolean;
}

export function Sidebar({ activePage, onNavigate, userName, onLogout }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation('admin');
  const perms = usePermissions();

  const menuItems: MenuItem[] = [
    { id: 'dashboard', label: t('sidebar_dashboard'), icon: '📊', permission: { resource: 'dashboard', action: 'read' } },
    { id: 'live', label: t('sidebar_live'), icon: '📡', permission: { resource: 'gps', action: 'read' } },
    { id: 'fleet', label: t('sidebar_fleet'), icon: '🔧', permission: { resource: 'fleet', action: 'read' }, divider: true },
    { id: 'buses', label: t('sidebar_buses'), icon: '🚌', permission: { resource: 'buses', action: 'read' } },
    { id: 'routes', label: t('sidebar_routes'), icon: '🗺️', permission: { resource: 'routes', action: 'read' } },
    { id: 'students', label: t('sidebar_students'), icon: '🎓', permission: { resource: 'students', action: 'read' } },
    { id: 'trips', label: t('sidebar_trips'), icon: '🚏', permission: { resource: 'trips', action: 'read' } },
    { id: 'safety', label: t('sidebar_safety'), icon: '🛡️', permission: { resource: 'safety', action: 'read' }, divider: true },
    { id: 'users', label: t('sidebar_users'), icon: '👥', permission: { resource: 'users', action: 'read' } },
    { id: 'chat', label: t('sidebar_chat'), icon: '💬', permission: { resource: 'chat', action: 'read' } },
    { id: 'reports', label: t('sidebar_reports'), icon: '📈', permission: { resource: 'reports', action: 'read' } },
    { id: 'feedback', label: t('sidebar_feedback'), icon: '📝', permission: { resource: 'feedback', action: 'manage' }, divider: true },
    { id: 'billing', label: t('sidebar_billing'), icon: '💳', permission: { resource: 'billing', action: 'read' } },
    { id: 'settings', label: t('sidebar_settings'), icon: '⚙️', permission: { resource: 'settings', action: 'read' } },
    { id: 'audit', label: t('sidebar_audit'), icon: '📋', permission: { resource: 'settings', action: 'read' } },
    { id: 'organization', label: 'Organization', icon: '🏛️', roles: ['super_admin', 'school_admin', 'organization_admin'] },
    { id: 'licenses', label: 'Licenses', icon: '🔑', roles: ['super_admin'] },
    { id: 'superadmin', label: t('sidebar_superadmin'), icon: '🏢', roles: ['super_admin'] },
    { id: 'replay', label: t('sidebar_replay'), icon: '⏪', permission: { resource: 'replay', action: 'read' } },
  ];

  const visibleItems = menuItems.filter(item => {
    if (item.roles) return perms.hasRole(...item.roles);
    if (item.permission) return perms.hasPermission(item.permission.resource, item.permission.action);
    return true;
  });

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand" onClick={() => setCollapsed(!collapsed)}>
          <span className="sidebar-logo">🚌</span>
          {!collapsed && <span className="sidebar-title">Bus Tracker</span>}
        </div>
        {!collapsed && <span className="version-tag">{APP_VERSION}</span>}
      </div>

      <nav className="sidebar-nav">
        {visibleItems.map((item) => (
          <div key={item.id}>
            {item.divider && <div className="sidebar-divider" />}
            <button
              className={`sidebar-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => onNavigate(item.id)}
              title={item.label}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!collapsed && <span className="sidebar-label">{item.label}</span>}
            </button>
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <span className="sidebar-icon">👤</span>
          {!collapsed && (
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{userName}</span>
              <span className="sidebar-user-role">{perms.roles[0] || 'user'}</span>
            </div>
          )}
        </div>
        <button className="sidebar-item sidebar-logout" onClick={onLogout} title={t('sidebar_logout', { ns: 'common', defaultValue: 'Logout' })}>
          <span className="sidebar-icon">🚪</span>
          {!collapsed && <span className="sidebar-label">{t('logout', { ns: 'common' })}</span>}
        </button>
      </div>
    </aside>
  );
}
